import React from 'react';
import clsx from 'clsx';
import { Snackbar, SnackbarProps, Typography } from '@material-ui/core';
import { Alert, AlertProps, AlertTitle } from '@material-ui/lab';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import './Notification.scss';
import { pxToRem } from '@fluentsms/agentnet-web-components';

export interface NotificationProps extends AlertProps {
  error?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  title?: string;
  msg?: string;
  action?: React.ReactNode;
  handleClose?: () => void;
  snackbar?: boolean;
  alertClass?: string;
  snackBarClass?: string;
  inline?: boolean;
  duration?: number;
  disableYPos?: boolean;
  sticky?: boolean;
  stickyClass?: string;
  disablePadding?: boolean;
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { borderWidth: 1, borderStyle: 'solid', color: '#FF9800', width: '100%' },
    alert: {
      padding: '8px 16px',
      '& li': {
        padding: '0 8px',
        margin: 0,
      },
    },
    alertTitle: {
      fontWeight: 500,
      fontSize: pxToRem(16),
      lineHeight: pxToRem(19),
      color: theme.palette.text.primary,
    },
    alertWrapInline: {
      position: 'relative',
      width: '100%',
      boxShadow: 'none',
      filter: 'none',
      '& .MuiAlert-icon': {
        padding: '6px 0 0 16px',
      },
    },
    alertWrapStandalone: {
      position: 'absolute',
      width: '100%',
      top: 0,
      maxWidth: 800,
    },
    disablePadding: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    info: {
      backgroundColor: '#EAF4FC',
      color: '#0D3C61',
    },
    infoText: {
      color: '#0D3C61',
    },
  }),
);

export const Notification = (props: NotificationProps, sProps: SnackbarProps): JSX.Element => {
  const classes = useStyles();
  const {
    disablePadding,
    disableYPos,
    sticky,
    stickyClass,
    snackBarClass,
    alertClass,
    inline,
    duration,
    snackbar,
    title,
    msg,
    ...alertProps
  } = props;

  return snackbar ? (
    <div className={`snackbar-wrapper ${snackBarClass ? snackBarClass : ''}`}>
      <Snackbar
        className={`snackbar-fixed ${disableYPos ? 'snackbar-disable-transform' : ''}`}
        open={true}
        autoHideDuration={duration ? duration : 6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        {...sProps}
      >
        <Alert
          className={`alert-wrap ${alertClass ? alertClass : ''}`}
          severity={props.severity}
          {...alertProps}
          onClose={props.handleClose}
        >
          {title && <AlertTitle>{title}</AlertTitle>}
          {msg}
        </Alert>
      </Snackbar>
    </div>
  ) : sticky ? (
    <div>
      <Snackbar
        open={true}
        hidden={false}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        {...sProps}
        className={`${stickyClass ? stickyClass : ''}`}
      >
        <Alert className={classes.root} severity={props.severity} {...alertProps} onClose={props.handleClose}>
          {title && <AlertTitle className={classes.alertTitle}>{title}</AlertTitle>}
          <Typography variant="body2" color="textSecondary">
            {msg}
          </Typography>
        </Alert>
      </Snackbar>
    </div>
  ) : (
    <Alert
      className={clsx(
        classes.alert,
        inline ? classes.alertWrapInline : classes.alertWrapStandalone,
        alertClass ? alertClass : '',
        { [classes.disablePadding]: disablePadding, [classes.info]: props.severity === 'info' },
      )}
      severity={props.severity}
      {...alertProps}
      onClose={props.handleClose}
    >
      {title && (
        <AlertTitle className={clsx(classes.alertTitle, { [classes.infoText]: props.severity === 'info' })}>
          {title}
        </AlertTitle>
      )}
      <Typography
        variant="body2"
        color="textSecondary"
        className={clsx({ [classes.infoText]: props.severity === 'info' })}
      >
        {msg}
        {props.children}
      </Typography>
    </Alert>
  );
};
